import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { WeavePortalLogo } from '@frontend/assets';
import { authnClient } from '@frontend/auth';
import { SignInMethod } from '@frontend/auth-helpers';
import { useRemoveAppLoader } from '@frontend/document';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ContentLoader, TextLink } from '@frontend/design-system';
import { LegacySignIn } from '../components/legacy';
import { OIDCSignIn, useOIDCSignIn } from '../components/OIDCSignIn';

export const SignInView = () => {
  const { t } = useTranslation('SignIn');
  const [isReady, setIsReady] = useState(false);
  const [signInMethod, setSignInMethod] = useState<SignInMethod>('oidc');
  useRemoveAppLoader();

  //this will automatically redirect to the OIDC login page if the sign-in method is oidc
  const { message: oidcMessage, redirecting: oidcRedirecting } = useOIDCSignIn(isReady && signInMethod === 'oidc');

  useEffect(() => {
    authnClient
      .getSignInMethod()
      .then((method) => {
        const initMethod = method === 'okta' ? 'oidc' : method;
        authnClient.changeAuthMethod(initMethod);
        setSignInMethod(initMethod);
      })
      .finally(() => {
        setIsReady(true);
      });
  }, []);

  const handleSignInMethodChange = () => {
    switch (signInMethod) {
      case 'oidc':
        setSignInMethod(authnClient.changeAuthMethod('legacy'));
        return;
      case 'legacy':
        setSignInMethod(authnClient.changeAuthMethod('oidc'));
        return;
      default:
        setSignInMethod(authnClient.changeAuthMethod('oidc'));
        return;
    }
  };

  if ((signInMethod === 'oidc' && !isReady) || (signInMethod === 'oidc' && oidcRedirecting)) {
    return null; //we don't want to show any flashes of loading in this case
  }
  if (!isReady) {
    return <ContentLoader message={t('Checking...')} show />;
  }

  return (
    <div
      css={css`
        align-items: center;
        display: flex;
        flex: 1;
        background-color: ${theme.colors.neutral5};
        justify-content: center;
      `}
    >
      <main
        css={css`
          border: 1px solid ${theme.colors.neutral20};
          border-radius: ${theme.borderRadius.medium};
          display: flex;
          flex-direction: column;
          justify-content: center;
          background-color: ${theme.colors.white};
          padding: ${theme.spacing(5)};
        `}
      >
        <WeavePortalLogo />

        <div
          css={css`
            display: flex;
            flex-direction: column;
            width: ${theme.spacing(43)};
          `}
        >
          {(() => {
            switch (signInMethod) {
              case 'legacy':
                return <LegacySignIn />;
              default:
                return <OIDCSignIn message={oidcMessage} />;
            }
          })()}

          <TextLink
            css={css`
              cursor: pointer;
              font-size: ${theme.fontSize(12)};
            `}
            onClick={handleSignInMethodChange}
          >
            {t('Change Sign In Method')}
          </TextLink>
        </div>
      </main>
    </div>
  );
};
